import { z } from 'zod';
import { apiClient } from '@api/client';
import type { ApiResponseShape, CommonResponse } from '@/types/api';
import {
  closureDetailSchema,
  closureSchema,
  type Closure,
  type ClosureDetail,
  type ClosureFormValues,
  type ListClosuresParams,
} from './schema';

const BASE_PATH = '/closures';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(closureSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const detailResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: closureDetailSchema.nullable(),
  error: z.string().nullable().optional(),
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: closureSchema.nullable(),
  error: z.string().nullable().optional(),
});

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

// Drop fully-empty rows; half-filled is already blocked at zod.
function toWireTranslations(values: ClosureFormValues) {
  return values.translations
    .filter((t) => t.name.trim().length > 0)
    .map((t) => ({
      localeCode: t.localeCode,
      name: t.name.trim(),
      description:
        t.description.trim().length > 0 ? t.description.trim() : null,
    }));
}

function toCreatePayload(values: ClosureFormValues) {
  return {
    name: values.name,
    description: values.description,
    startDate: values.startDate,
    endDate: values.endDate,
    isActive: values.isActive,
    apmcIds: values.apmcIds,
    translations: toWireTranslations(values),
  };
}

function toUpdatePayload(values: ClosureFormValues) {
  // Same shape — server treats all fields as optional on PUT and
  // apmcIds/translations as replace-on-present.
  return toCreatePayload(values);
}

export async function listClosures(
  params: ListClosuresParams,
): Promise<CommonResponse<Closure>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function getClosure(id: string): Promise<ClosureDetail> {
  const response = await apiClient.get(`${BASE_PATH}/${id}`);
  const parsed = detailResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function createClosure(
  values: ClosureFormValues,
): Promise<Closure> {
  const response = await apiClient.post<ApiResponseShape<Closure>>(
    BASE_PATH,
    toCreatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function updateClosure(
  id: string,
  values: ClosureFormValues,
): Promise<Closure> {
  const response = await apiClient.put<ApiResponseShape<Closure>>(
    `${BASE_PATH}/${id}`,
    toUpdatePayload(values),
  );
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty response' : parsed.error.message);
  }
  return parsed.data.data;
}

export async function deleteClosure(id: string): Promise<void> {
  await apiClient.delete(`${BASE_PATH}/${id}`);
}
